import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getOverview, Granularity, Overview as OverviewData } from "../../Services/stats.service.ts";
import styles from "./Overview.module.css";

type Preset = "today" | "7d" | "30d" | "month" | "year" | "all" | "custom";

const PRESETS: Array<{ id: Preset; label: string }> = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "month", label: "This month" },
  { id: "year", label: "This year" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom" },
];

const GRANULARITIES: Array<{ id: Granularity; label: string }> = [
  { id: "auto", label: "Auto" },
  { id: "day", label: "Day" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

const INK = "#1a1a1a";
const MUTE = "#777";
const LINE = "#e8e5df";
const SAND = "#cfc6b8";
const STATUS_COLORS: Record<string, string> = {
  Pending: "#9a9284",
  Processing: "#5b6b8c",
  Shipped: "#7a6aa8",
  Delivered: "#3f7d5a",
  Cancelled: "#b5523b",
};

const pkToday = () => new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
const shiftDay = (day: string, days: number) => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const rangeFor = (preset: Preset, custom: { from: string; to: string }) => {
  const today = pkToday();
  if (preset === "today") return { from: today, to: today };
  if (preset === "7d") return { from: shiftDay(today, -6), to: today };
  if (preset === "30d") return { from: shiftDay(today, -29), to: today };
  if (preset === "month") return { from: `${today.slice(0, 7)}-01`, to: today };
  if (preset === "year") return { from: `${today.slice(0, 4)}-01-01`, to: today };
  if (preset === "custom") return custom;
  return { range: "all" as const };
};

const rs = (value: number) => `Rs ${Math.round(value || 0).toLocaleString("en-PK")}`;
const compact = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(Math.round(value));
};
const num = (value: number) => Math.round(value || 0).toLocaleString("en-PK");
const pretty = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

const Change = ({ now, before }: { now: number; before: number }) => {
  if (!before && !now) return <span className={styles.flat}>No change</span>;
  if (!before) return <span className={styles.up}>New</span>;
  const pct = ((now - before) / before) * 100;
  const label = `${pct >= 0 ? "+" : ""}${pct.toFixed(pct > -10 && pct < 10 ? 1 : 0)}%`;
  return <span className={pct >= 0 ? styles.up : styles.down}>{label}</span>;
};

const ChartTip = ({ active, payload, label, money }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tip}>
      {label !== undefined && <div className={styles.tipLabel}>{label}</div>}
      {payload.map((row: any) => (
        <div key={row.dataKey} className={styles.tipRow}>
          <span className={styles.tipDot} style={{ background: row.color || row.payload?.fill || INK }} />
          <span>{row.name}</span>
          <b>{money?.includes(row.dataKey) ? rs(row.value) : num(row.value)}</b>
        </div>
      ))}
    </div>
  );
};

const Panel = ({
  title,
  note,
  children,
  wide,
  order,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
  wide?: boolean;
  order?: number;
}) => (
  <section className={`${styles.panel} ${wide ? styles.wide : ""}`} style={order ? { order } : undefined}>
    <header className={styles.panelHead}>
      <h3>{title}</h3>
      {note && <span>{note}</span>}
    </header>
    {children}
  </section>
);

const Empty = ({ children = "No sales in this period" }: { children?: React.ReactNode }) => (
  <div className={styles.empty}>{children}</div>
);

const Overview = ({ onOpenOrders }: { onOpenOrders?: () => void }) => {
  const [preset, setPreset] = useState<Preset>("30d");
  const [custom, setCustom] = useState(() => ({ from: shiftDay(pkToday(), -29), to: pkToday() }));
  const [granularity, setGranularity] = useState<Granularity>("auto");
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [trendMetric, setTrendMetric] = useState<"revenue" | "orders" | "pieces">("revenue");

  const query = useMemo(() => rangeFor(preset, custom), [preset, custom]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getOverview({ ...query, granularity }).then((result) => {
      if (!alive) return;
      if (result.success && result.data) {
        setData(result.data);
        setError("");
      } else {
        setError(result.error || "Could not load statistics.");
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [query, granularity]);

  const k = data?.kpis;
  const p = data?.previous;
  const hasSales = !!k && k.orders > 0;
  const statusTotal = data?.status.reduce((sum, row) => sum + row.count, 0) || 0;
  const maxProductRevenue = data?.topProducts[0]?.revenue || 1;
  const maxCityRevenue = data?.cities[0]?.revenue || 1;
  const metricLabel = { revenue: "Revenue", orders: "Orders", pieces: "Pieces" }[trendMetric];
  const hourData =
    data?.hours.map((row) => ({
      ...row,
      label: `${row.hour % 12 || 12}${row.hour < 12 ? "am" : "pm"}`,
    })) || [];

  return (
    <div className={styles.wrap}>
      <div className={styles.top}>
        <div>
          <h2 className={styles.title}>Overview</h2>
          {data && (
            <p className={styles.sub}>
              {pretty(data.range.from)} – {pretty(data.range.to)}
              {p && preset !== "all" && (
                <> · compared with {pretty(p.from)} – {pretty(p.to)}</>
              )}
            </p>
          )}
        </div>
        <div className={styles.controls}>
          <div className={styles.segment} role="tablist" aria-label="Date range">
            {PRESETS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={preset === item.id ? styles.on : ""}
                onClick={() => setPreset(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className={styles.dates}>
              <input
                type="date"
                value={custom.from}
                max={custom.to}
                onChange={(event) => event.target.value && setCustom((c) => ({ ...c, from: event.target.value }))}
                aria-label="From date"
              />
              <span>to</span>
              <input
                type="date"
                value={custom.to}
                min={custom.from}
                max={pkToday()}
                onChange={(event) => event.target.value && setCustom((c) => ({ ...c, to: event.target.value }))}
                aria-label="To date"
              />
            </div>
          )}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {data && (
        <div className={`${styles.body} ${loading ? styles.loading : ""}`}>
          <div className={styles.snapshot}>
            {(
              [
                ["Today", data.snapshot.today],
                ["This month", data.snapshot.month],
                ["This year", data.snapshot.year],
                ["All time", data.snapshot.all],
              ] as const
            ).map(([label, row]) => (
              <div key={label} className={styles.snap}>
                <span>{label}</span>
                <b>{rs(row.revenue)}</b>
                <small>
                  {num(row.orders)} {row.orders === 1 ? "order" : "orders"} · {num(row.pieces)} pcs
                </small>
              </div>
            ))}
          </div>

          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span>Revenue</span>
              <b>{rs(k!.revenue)}</b>
              <Change now={k!.revenue} before={p!.revenue} />
            </div>
            <div className={styles.kpi}>
              <span>Orders</span>
              <b>{num(k!.orders)}</b>
              <Change now={k!.orders} before={p!.orders} />
            </div>
            <div className={styles.kpi}>
              <span>Avg. order value</span>
              <b>{rs(k!.aov)}</b>
              <Change now={k!.aov} before={p!.aov} />
            </div>
            <div className={styles.kpi}>
              <span>Pieces sold</span>
              <b>{num(k!.pieces)}</b>
              <Change now={k!.pieces} before={p!.pieces} />
            </div>
            <div className={styles.kpi}>
              <span>Customers</span>
              <b>{num(k!.customers)}</b>
              <small>
                {num(k!.newCustomers)} new · {num(k!.repeatCustomers)} repeat
              </small>
            </div>
            <div className={styles.kpi}>
              <span>Cancellation rate</span>
              <b>{k!.cancelRate}%</b>
              <small>{num(k!.cancelled)} cancelled</small>
            </div>
          </div>

          <div className={styles.money}>
            <div>
              <span>Product sales</span>
              <b>{rs(k!.itemsRevenue)}</b>
            </div>
            <div>
              <span>Delivery charges</span>
              <b>{rs(k!.deliveryFees)}</b>
            </div>
            <div>
              <span>Discounts given</span>
              <b>{rs(k!.discounts)}</b>
            </div>
            <div>
              <span>Cash collected</span>
              <b>{rs(k!.collected)}</b>
              <small>Delivered orders</small>
            </div>
            <div>
              <span>Awaiting delivery</span>
              <b>{rs(k!.inPipeline)}</b>
              <small>Pending, processing, shipped</small>
            </div>
          </div>

          <div className={styles.grid}>
            <Panel title="Sales trend" wide order={1}>
              <div className={styles.panelTools}>
                <div className={styles.segmentSmall}>
                  {(["revenue", "orders", "pieces"] as const).map((metric) => (
                    <button
                      key={metric}
                      type="button"
                      className={trendMetric === metric ? styles.on : ""}
                      onClick={() => setTrendMetric(metric)}
                    >
                      {{ revenue: "Revenue", orders: "Orders", pieces: "Pieces" }[metric]}
                    </button>
                  ))}
                </div>
                <div className={styles.segmentSmall}>
                  {GRANULARITIES.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={granularity === item.id ? styles.on : ""}
                      onClick={() => setGranularity(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.chartTall}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={INK} stopOpacity={0.16} />
                        <stop offset="100%" stopColor={INK} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={LINE} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: MUTE, fontSize: 11 }}
                      tickLine={false}
                      axisLine={{ stroke: LINE }}
                      minTickGap={16}
                    />
                    <YAxis
                      tick={{ fill: MUTE, fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      width={48}
                      allowDecimals={false}
                      tickFormatter={(value) => (trendMetric === "revenue" ? compact(value) : String(value))}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(26,26,26,0.04)" }}
                      content={<ChartTip money={["revenue"]} />}
                    />
                    {data.series.length > 24 ? (
                      <Area
                        type="monotone"
                        dataKey={trendMetric}
                        name={metricLabel}
                        stroke={INK}
                        strokeWidth={2}
                        fill="url(#trendFill)"
                      />
                    ) : (
                      <Bar dataKey={trendMetric} name={metricLabel} fill={INK} radius={[4, 4, 0, 0]} maxBarSize={44} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Order status" note={`${num(statusTotal)} orders`} order={2}>
              {statusTotal ? (
                <div className={styles.donutWrap}>
                  <div className={styles.donut}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.status.filter((row) => row.count > 0)}
                          dataKey="count"
                          nameKey="name"
                          innerRadius="62%"
                          outerRadius="100%"
                          paddingAngle={2}
                          stroke="none"
                        >
                          {data.status
                            .filter((row) => row.count > 0)
                            .map((row) => (
                              <Cell key={row.name} fill={STATUS_COLORS[row.name] || SAND} />
                            ))}
                        </Pie>
                        <Tooltip content={<ChartTip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className={styles.donutCenter}>
                      <b>{num(statusTotal)}</b>
                      <span>orders</span>
                    </div>
                  </div>
                  <ul className={styles.legend}>
                    {data.status.map((row) => (
                      <li key={row.name}>
                        <i style={{ background: STATUS_COLORS[row.name] || SAND }} />
                        <span>{row.name}</span>
                        <b>{num(row.count)}</b>
                        <small>{rs(row.value)}</small>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <Empty>No orders in this period</Empty>
              )}
            </Panel>

            <Panel
              title="Top products"
              note={hasSales ? `${num(data.productCount)} products sold` : undefined}
              wide
              order={3}
            >
              {data.topProducts.length ? (
                <div className={styles.table}>
                  <div className={`${styles.row} ${styles.head}`}>
                    <span>Product</span>
                    <span>Pieces</span>
                    <span>Orders</span>
                    <span>Revenue</span>
                  </div>
                  {data.topProducts.map((row, index) => (
                    <div key={row.id || row.name} className={styles.row}>
                      <span className={styles.product}>
                        <em>{index + 1}</em>
                        {row.image ? <img src={row.image} alt="" /> : <i className={styles.noImg} />}
                        <span className={styles.productName}>
                          {row.id ? (
                            <a href={`/product/${row.id}`} target="_blank" rel="noreferrer">
                              {row.name}
                            </a>
                          ) : (
                            row.name
                          )}
                          <i
                            className={styles.meter}
                            style={{ width: `${Math.max(4, (row.revenue / maxProductRevenue) * 100)}%` }}
                          />
                        </span>
                      </span>
                      <span>{num(row.pieces)}</span>
                      <span>{num(row.orders)}</span>
                      <span className={styles.strong}>{rs(row.revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <Empty />
              )}
            </Panel>

            <Panel title="Sales by province" order={4}>
              {data.provinces.length ? (
                <div className={styles.chartAuto} style={{ height: Math.max(160, data.provinces.length * 38) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={data.provinces}
                      layout="vertical"
                      margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={124}
                        tick={{ fill: INK, fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip cursor={{ fill: "rgba(26,26,26,0.04)" }} content={<ChartTip money={["revenue"]} />} />
                      <Bar dataKey="revenue" name="Revenue" fill={INK} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty />
              )}
              {data.countries.length > 0 && (
                <ul className={`${styles.plain} ${styles.countries}`}>
                  {data.countries.map((row) => (
                    <li key={row.name}>
                      <span>{row.name}</span>
                      <small>
                        {num(row.orders)} {row.orders === 1 ? "order" : "orders"}
                      </small>
                      <b>{rs(row.revenue)}</b>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Top cities" note={hasSales ? `${num(data.cityCount)} cities` : undefined} order={5}>
              {data.cities.length ? (
                <ul className={styles.bars}>
                  {data.cities.slice(0, 8).map((row) => (
                    <li key={row.name}>
                      <div>
                        <span>
                          {row.name}
                          <small>{row.province}</small>
                        </span>
                        <b>{rs(row.revenue)}</b>
                      </div>
                      <i style={{ width: `${Math.max(3, (row.revenue / maxCityRevenue) * 100)}%` }} />
                      <small>
                        {num(row.orders)} {row.orders === 1 ? "order" : "orders"}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty />
              )}
            </Panel>

            <Panel title="Sales by category" wide order={11}>
              {data.byCategory.length ? (
                <div className={styles.chartAuto} style={{ height: Math.max(140, data.byCategory.length * 44) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.byCategory} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={124}
                        tick={{ fill: INK, fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(26,26,26,0.04)" }}
                        content={<ChartTip money={["revenue"]} />}
                      />
                      <Bar dataKey="revenue" name="Revenue" fill={INK} radius={[0, 4, 4, 0]} barSize={16} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty />
              )}
            </Panel>

            <Panel title="Sizes sold" note="Pieces" order={10}>
              {data.bySize.length ? (
                <div className={styles.chartMid}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.bySize} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={LINE} vertical={false} />
                      <XAxis dataKey="size" tick={{ fill: INK, fontSize: 12 }} tickLine={false} axisLine={{ stroke: LINE }} />
                      <YAxis tick={{ fill: MUTE, fontSize: 11 }} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(26,26,26,0.04)" }} content={<ChartTip />} />
                      <Bar dataKey="pieces" name="Pieces" fill={INK} radius={[4, 4, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty />
              )}
            </Panel>

            <Panel title="Orders by weekday" order={8}>
              {hasSales ? (
                <div className={styles.chartMid}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.weekday} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={LINE} vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: INK, fontSize: 12 }} tickLine={false} axisLine={{ stroke: LINE }} />
                      <YAxis tick={{ fill: MUTE, fontSize: 11 }} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(26,26,26,0.04)" }} content={<ChartTip money={["revenue"]} />} />
                      <Bar dataKey="orders" name="Orders" fill={INK} radius={[4, 4, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty />
              )}
            </Panel>

            <Panel title="Orders by time of day" note="Pakistan time" wide order={9}>
              {hasSales ? (
                <div className={styles.chartMid}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={hourData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="hourFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={INK} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={INK} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={LINE} vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: MUTE, fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: LINE }}
                        interval={2}
                      />
                      <YAxis tick={{ fill: MUTE, fontSize: 11 }} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
                      <Tooltip content={<ChartTip />} />
                      <Area type="monotone" dataKey="orders" name="Orders" stroke={INK} strokeWidth={2} fill="url(#hourFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty />
              )}
            </Panel>

            <Panel title="Inventory" note={`${num(data.inventory.products)} products`} order={6}>
              <div className={styles.stock}>
                <div>
                  <b>{num(data.inventory.outOfStock)}</b>
                  <span>Out of stock</span>
                </div>
                <div>
                  <b>{num(data.inventory.lowStock)}</b>
                  <span>3 or fewer left</span>
                </div>
              </div>
              {data.inventory.attention.length ? (
                <ul className={styles.attention}>
                  {data.inventory.attention.map((row) => (
                    <li key={row.id}>
                      {row.image ? <img src={row.image} alt="" /> : <i className={styles.noImg} />}
                      <span>{row.name}</span>
                      <b className={row.out ? styles.outTag : styles.lowTag}>
                        {row.out ? "Out" : `${row.stock} left`}
                      </b>
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty>All products are well stocked</Empty>
              )}
            </Panel>

            <Panel title="Latest orders" order={7}>
              {data.recent.length ? (
                <>
                  <ul className={styles.recent}>
                    {data.recent.map((row) => (
                      <li key={row.id}>
                        <div>
                          <b>{row.name || "Customer"}</b>
                          <small>
                            {row.city} ·{" "}
                            {new Date(row.createdAt).toLocaleString("en-GB", {
                              day: "numeric",
                              month: "short",
                              hour: "numeric",
                              minute: "2-digit",
                              timeZone: "Asia/Karachi",
                            })}
                          </small>
                        </div>
                        <div className={styles.recentRight}>
                          <b>{rs(row.total)}</b>
                          <span
                            className={styles.statusPill}
                            style={{ color: STATUS_COLORS[row.status] || MUTE, borderColor: STATUS_COLORS[row.status] || LINE }}
                          >
                            {row.status}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {onOpenOrders && (
                    <button type="button" className={styles.linkButton} onClick={onOpenOrders}>
                      View all orders
                    </button>
                  )}
                </>
              ) : (
                <Empty>No orders in this period</Empty>
              )}
            </Panel>
          </div>
        </div>
      )}

      {!data && loading && <div className={styles.placeholder}>Loading statistics…</div>}
    </div>
  );
};

export default Overview;
